/**
 * LessonRight Component
 *
 * Features:
 * - Displays the right panel of the lesson page with tabs for activities, forum, notes, and transcript search.
 * - Integrates with Material-UI Tabs and custom icons for navigation.
 * - Passes relevant props to child components for lesson data, progress, and actions.
 * - Handles tab switching and conditional rendering of each section.
 * - Uses CSS modules for styling.
 *
 * @param {Object} props - Component properties
 * @param {Object} [props.lessonInfo] - Lesson info object from Redux
 * @param {number} [props.view] - Current view mode
 * @param {function} [props.changeView] - Function to change the view
 * @param {number} [props.videoProgress] - Progress for video activity
 * @param {number} [props.quizProgress] - Progress for quiz activity
 * @param {number} [props.homeworkProgress] - Progress for homework activity
 * @param {number} [props.coevaluationProgress] - Progress for peer evaluation
 * @param {bool} [props.forumEnabled] - Whether the forum tab is enabled
 * @param {number} [props.idLesson] - Lesson ID
 * @param {number} [props.idCourse] - Course ID
 * @param {Array} [props.transcript] - Array of transcript objects
 * @param {number} [props.playerTime] - Current time of the video player
 * @param {string} [props.format] - Lesson format (e.g., 'AUDIO', 'VIDEO')
 * @returns {JSX.Element} Rendered LessonRight component
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import CreateIcon from '@mui/icons-material/Create';
import CommentIcon from '@mui/icons-material/Comment';
import SearchIcon from '@mui/icons-material/Search';

import Activities from './Activities/Activities';
import Forum from './Forum/Forum';
import NotesContainer from './NotesContainer/NotesContainer';
import SearchInLesson from './SearchInLesson/SearchInLesson';

import classes from './LessonRight.module.css';

const LessonRight = props => {
  const { t } = useTranslation(['lesson']);
  const dataLesson = useSelector(state => state.lessonInfo);
  const [value, setValue] = useState(1);

  const handleChange = (event, newValue) => {
    if (newValue === 3 || newValue === 4) {
      props.changeView(1);
    }
    setValue(newValue);
  };

  return (
    <div className={classes.lessonRightContainer}>
      <Tabs
        value={value}
        onChange={handleChange}
        TabIndicatorProps={{ style: { background: '#890f20' } }}
      >
        <Tab
          className={`${classes.tab} ${
            value === 1 && classes.itemSelected
          }`}
          value={1}
          icon={<FormatListBulletedIcon />}
          label={t('activities')}
        />
        <Tab
          className={
            !props.forumEnabled
              ? `${classes.tabNoShow}`
              : `${classes.tab} ${
                  value === 2 && classes.itemSelected
                }`
          }
          value={2}
          icon={<CommentIcon />}
          label={t('forum')}
        />
        <Tab
          className={`${classes.tab} ${
            value === 3 && classes.itemSelected
          }`}
          value={3}
          icon={<CreateIcon />}
          label={t('note')}
        />
        {props.transcript.length > 0 && (
          <Tab
            className={`${classes.tab} ${
              value === 4 && classes.itemSelected
            }`}
            value={4}
            icon={<SearchIcon />}
            label="Busqueda"
          />
        )}
      </Tabs>
      {/* Custom component: Activities receives lessonData, material, lessonProgress, changeView, videoProgress, quizProgress, homeworkProgress, coevaluationProgress, format */}
      {value === 1 && (
        <Activities
          lessonData={dataLesson.data}
          material={dataLesson.files.rows}
          lessonProgress={dataLesson.lessonProgress}
          changeView={props.changeView}
          videoProgress={props.videoProgress}
          quizProgress={props.quizProgress}
          homeworkProgress={props.homeworkProgress}
          coevaluationProgress={props.coevaluationProgress}
          format={props.format}
        />
      )}
      {/* Custom component: Forum receives idLesson, idCourse */}
      {value === 2 && (
        <Forum idLesson={props.idLesson} idCourse={props.idCourse} />
      )}
      {/* Custom component: NotesContainer receives view, idLesson */}
      {value === 3 && (
        <NotesContainer
          view={props.view}
          idLesson={props.idLesson}
        />
      )}
      {/* Custom component: SearchInLesson receives searchTexts, playerTime */}
      {value === 4 && (
        <SearchInLesson
          searchTexts={props.transcript}
          playerTime={props.playerTime}
        />
      )}
    </div>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
LessonRight.propTypes = {
  /** Lesson info object from Redux */
  lessonInfo: PropTypes.object,
  /** Current view mode */
  view: PropTypes.number,
  /** Function to change the view */
  changeView: PropTypes.func,
  /** Progress for video activity */
  videoProgress: PropTypes.number,
  /** Progress for quiz activity */
  quizProgress: PropTypes.number,
  /** Progress for homework activity */
  homeworkProgress: PropTypes.number,
  /** Progress for peer evaluation */
  coevaluationProgress: PropTypes.number,
  /** Whether the forum tab is enabled */
  forumEnabled: PropTypes.bool,
  /** Lesson ID */
  idLesson: PropTypes.number,
  /** Course ID */
  idCourse: PropTypes.number,
  /** Array of transcript objects */
  transcript: PropTypes.array,
  /** Current time of the video player */
  playerTime: PropTypes.number,
  /** Lesson format (e.g., 'AUDIO', 'VIDEO') */
  format: PropTypes.string
};

LessonRight.defaultProps = {
  lessonInfo: undefined,
  view: undefined,
  changeView: undefined,
  videoProgress: undefined,
  quizProgress: undefined,
  homeworkProgress: undefined,
  coevaluationProgress: undefined,
  forumEnabled: undefined,
  idLesson: undefined,
  idCourse: undefined,
  transcript: undefined,
  playerTime: undefined,
  format: undefined
};

export default LessonRight;
